import React, { useEffect, useState, useCallback } from "react";
import { applyGroveUpdate } from "@/registerServiceWorker";

/**
 * A small unobtrusive banner that appears at the bottom of the screen when a
 * new service-worker version is ready to take over. Tapping the button
 * activates the new SW and reloads the page exactly once.
 *
 * Dismissable: if the user taps "later", the banner hides until the next
 * update arrives or the page is fully reloaded.
 */
export default function UpdateBanner() {
  const [registration, setRegistration] = useState(null);

  useEffect(() => {
    const handler = (event) => {
      setRegistration(event.detail.registration);
    };
    window.addEventListener("rfg:sw-update-ready", handler);
    return () => window.removeEventListener("rfg:sw-update-ready", handler);
  }, []);

  const apply = useCallback(() => {
    applyGroveUpdate(registration);
  }, [registration]);

  const dismiss = useCallback(() => {
    setRegistration(null);
  }, []);

  if (!registration) return null;

  return (
    <div
      className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:max-w-sm z-50 animate-fade-in"
      data-testid="update-banner"
      role="alert"
    >
      <div className="bg-[#1C241F] text-[#F4F1E8] rounded-2xl shadow-lg border border-[#869960]/40 px-5 py-4 backdrop-blur-sm">
        <div className="text-[10px] tracking-[0.3em] uppercase text-[#869960] mb-1">
          ⟡ a new breath has arrived
        </div>
        <div className="font-serif text-base md:text-lg leading-snug mb-3">
          the grove has updated. tap to step into the freshest version.
        </div>
        <div className="flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={dismiss}
            className="text-[10px] tracking-[0.25em] uppercase text-[#9FA9A3] hover:text-[#F4F1E8] transition-colors px-2 py-1"
            data-testid="update-banner-dismiss"
          >
            later
          </button>
          <button
            type="button"
            onClick={apply}
            className="text-[10px] tracking-[0.25em] uppercase bg-[#869960] hover:bg-[#6E7F4D] text-[#F4F1E8] rounded-full px-4 py-2 transition-colors"
            data-testid="update-banner-apply"
          >
            refresh →
          </button>
        </div>
      </div>
    </div>
  );
}
